import React, { useMemo } from "react";
import CreateMissionWorkingTimeMore from "../../../screens/create-mission/CreateMissionWorkingTimeMore";
import { newMissionState } from "../../../atoms/core";
import { EarningType, Mission } from "../../../__generated__/graphql";
import MissionScreensManager from "../../../contexts/CreateMissionManager";
import { MockedProvider } from "@apollo/client/testing/react";
import { buildContract, buildLegalEmployer, buildMission, buildWorkerInfo } from "../../factories";
import type { Meta, StoryObj } from "@storybook/react";
import { GET_CCT_BY_EDITION_BY } from "../../../graphql/cct";
import { AtomsHydrator } from "../../../atoms/atoms-hydrator";

const InnerComponent = ({ mission }: { mission: Mission }) => {
    return (
        <AtomsHydrator atomValues={[[newMissionState, mission]]}>
            <CreateMissionWorkingTimeMore />
        </AtomsHydrator>
    );
};

const mocks = [
    {
        request: {
            query: GET_CCT_BY_EDITION_BY,
            variables: { cctEditionId: "1" },
        },
        result: () => ({
            data: {
                getCctByEditionId: {
                    __typename: "CCT",
                    id: "cct-1",
                    cctEditionId: "cct-1-A",
                    cctVersionId: "v-cct-1-A",
                    cctContractId: "cct-contract-1",
                    name: "My CCT 1",
                    hoursPerWeek: 42.5,
                    active: true,
                    intercomMainArticleId: "intercom-main-article-id",
                    intercomHelpArticleId: "intercom-help-article-id",
                    hoursPerWeekDefault: 42,
                },
            },
        }),
    },
];
function StoryComponent({
    earningType,
    useLseCct = true,
}: {
    earningType: EarningType;
    useLseCct?: boolean;
}) {
    const mission = useMemo(
        () =>
            buildMission({
                workerInfo: buildWorkerInfo({
                    birthdate: "1975-05-12",
                }),
                currentContract: buildContract({
                    earningType: earningType,
                    workingTimeClassicSpec: null,
                }),
                legalEmployer: buildLegalEmployer({ useLseCct }),
            }),
        [earningType, useLseCct],
    );
    return (
        <AtomsHydrator atomValues={[[newMissionState, mission]]}>
            <MockedProvider mocks={mocks}>
                <MissionScreensManager>
                    <InnerComponent mission={mission} />
                </MissionScreensManager>
            </MockedProvider>
        </AtomsHydrator>
    );
}

const meta = {
    title: "CreateMission/Working time",
    component: StoryComponent,
    argTypes: {
        earningType: { type: "symbol" },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const MonthlyClassic: Story = {
    args: {
        earningType: EarningType.Monthly,
    },
};

export const HourlyClassic: Story = {
    args: {
        earningType: EarningType.Hourly,
    },
};

// on demand is offered only outside LSE
export const HourlyNonLse: Story = {
    args: {
        earningType: EarningType.Hourly,
        useLseCct: false,
    },
};

export const DailyClassic: Story = {
    args: {
        earningType: EarningType.Daily,
    },
};

export const PackageClassic: Story = {
    args: {
        earningType: EarningType.Package,
    },
};
